import { useLayoutEffect } from "react";

type SelectableControl = HTMLInputElement | HTMLTextAreaElement;

const non_text_input_types = new Set([
  "button",
  "checkbox",
  "color",
  "date",
  "datetime-local",
  "file",
  "hidden",
  "image",
  "month",
  "radio",
  "range",
  "reset",
  "submit",
  "time",
  "week",
]);

function selectable_control(
  target: EventTarget | null,
): SelectableControl | null {
  if (target instanceof HTMLTextAreaElement) {
    return target;
  }
  if (
    target instanceof HTMLInputElement &&
    !non_text_input_types.has(target.type)
  ) {
    return target;
  }
  return null;
}

function select_if_focused(control: SelectableControl) {
  if (document.activeElement === control) {
    control.select();
  }
}

export function InputFocusSelection() {
  useLayoutEffect(() => {
    let pointer_is_down = false;
    let pointer_focused_control: SelectableControl | null = null;

    const handle_pointer_down = () => {
      pointer_is_down = true;
      pointer_focused_control = null;
    };
    const handle_focus_in = (event: FocusEvent) => {
      const control = selectable_control(event.target);
      if (!control) {
        return;
      }
      if (pointer_is_down) {
        pointer_focused_control = control;
      }
      window.requestAnimationFrame(() => select_if_focused(control));
    };
    const finish_pointer = () => {
      const control = pointer_focused_control;
      pointer_is_down = false;
      pointer_focused_control = null;
      if (control) {
        window.setTimeout(() => select_if_focused(control), 0);
      }
    };

    document.addEventListener("pointerdown", handle_pointer_down, true);
    document.addEventListener("focusin", handle_focus_in, true);
    document.addEventListener("pointerup", finish_pointer, true);
    document.addEventListener("pointercancel", finish_pointer, true);

    return () => {
      document.removeEventListener(
        "pointerdown",
        handle_pointer_down,
        true,
      );
      document.removeEventListener("focusin", handle_focus_in, true);
      document.removeEventListener("pointerup", finish_pointer, true);
      document.removeEventListener("pointercancel", finish_pointer, true);
    };
  }, []);

  return null;
}
